import * as React from "react";
import {Button, Grid, TextField} from "@material-ui/core";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import { AMAZON_ID } from "../../../../consts/Patterns";
import { HST_MIDNIGHT } from "../../../../consts/PacMan";

interface IUpdateBenefitDatesRequest {
    contractId: string
    consumptionStartDate: number
    billingDate: number
    cutOffDate: number
    drugId?: string
}

interface IProps {
    data?: any
}

interface IState {
    errorMessage: string
    isLoading: boolean
    request: IUpdateBenefitDatesRequest
    response: any
    consumptionStartDateStr: string
    billingDateStr: string
    cutOffDateStr: string
}

enum InputField {
    CONTRACT_ID,
    CONSUMPTION_START_DATE,
    BILLING_DATE,
    CUT_OFF_DATE,
    DRUG_ID
}

class UpdateBenefitDatesSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            errorMessage: '',
            isLoading: false,
            request: {} as IUpdateBenefitDatesRequest,
            response: '',
            consumptionStartDateStr: '',
            billingDateStr: '',
            cutOffDateStr: ''
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
    }

    public render() {
        return(
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >
                <Grid item xs>
                    <Alert variant="filled" severity="info">
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>Updates the active schedule associated with the provided contract id.</li>
                            <li>By default, both Packets and Standard benefit dates will be updated.</li>
                            <li>If a drug id is provided, only the benefit containing that drug id will be updated.</li>
                            <li>Selected dates are converted to midnight HST (Hawaii Standard Time) epoch milliseconds before submission.</li>
                        </ul>
                    </Alert>
                </Grid>

                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        label="Contract Id (Required)"
                        variant="outlined"
                        value={ this.state.request.contractId || '' }
                        onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.CONTRACT_ID, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Consumption Start Date (Required)"
                        variant="outlined"
                        value={ this.state.consumptionStartDateStr }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.CONSUMPTION_START_DATE, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Billing Date (Required)"
                        variant="outlined"
                        value={ this.state.billingDateStr }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.BILLING_DATE, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Cut Off Date (Required)"
                        variant="outlined"
                        value={ this.state.cutOffDateStr }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.CUT_OFF_DATE, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        label="Drug Id (Optional)"
                        variant="outlined"
                        value={ this.state.request.drugId || '' }
                        onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.DRUG_ID, event) }
                    />
                </Grid>

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={ this.handleSubmit }>
                        Submit
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="info">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response &&
                    <div>
                        <br/>
                        <h4>Status</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </p>
                    </div>
                }
            </Grid>
        );
    }

    private handleValidation() {
        if (!this.state.request.contractId) {
            alert('Please enter the contract id');
            return false;
        }
        if (!this.state.request.contractId.match(AMAZON_ID)) {
            alert('Please enter the valid contract id.');
            return false;
        }
        if (!this.state.consumptionStartDateStr) {
            alert('Please enter the consumption start date');
            return false;
        }
        if (!this.state.billingDateStr) {
            alert('Please enter the billing date');
            return false;
        }
        if (!this.state.cutOffDateStr) {
            alert('Please enter the cut off date');
            return false;
        }
        return true;
    }

    private setRequestFields(field: InputField, event: React.ChangeEvent<HTMLTextAreaElement>) {
        const updatedRequest: IUpdateBenefitDatesRequest = { ...this.state.request };

        switch (field) {
            case InputField.CONTRACT_ID: {
                updatedRequest.contractId = event.target.value;
                this.setState({ request: updatedRequest });
                break;
            }
            case InputField.CONSUMPTION_START_DATE: {
                this.setState({ consumptionStartDateStr: event.target.value });
                break;
            }
            case InputField.BILLING_DATE: {
                this.setState({ billingDateStr: event.target.value });
                break;
            }
            case InputField.CUT_OFF_DATE: {
                this.setState({ cutOffDateStr: event.target.value });
                break;
            }
            case InputField.DRUG_ID: {
                updatedRequest.drugId = event.target.value;
                this.setState({ request: updatedRequest });
                break;
            }
            default: {
                break;
            }
        }
    }

    private handleSubmit() {
        this.setState({ isLoading: true, errorMessage: '', response: '' });

        if (this.handleValidation()) {
            const body: any = {
                contractId: this.state.request.contractId,
                consumptionStartDate: new Date(this.state.consumptionStartDateStr + HST_MIDNIGHT).getTime(),
                billingDate: new Date(this.state.billingDateStr + HST_MIDNIGHT).getTime(),
                cutOffDate: new Date(this.state.cutOffDateStr + HST_MIDNIGHT).getTime()
            };

            if (this.state.request.drugId) {
                body.drugId = this.state.request.drugId;
            }

            const config = {
                body,
                headers: {}
            }
            this.callUpdateBenefitDates(config);
        } else {
            this.setState({ isLoading: false });
        }
    }

    private callUpdateBenefitDates(config: any) {
        const apiName = 'ApaqPortalAPI';
        const path = '/pharmacy-customer/update-benefit-dates';

        console.log(config)
        API.post(apiName, path, config).then((getResponse: any) => {
            console.log(getResponse);
            const parsed = { ...getResponse };
            if (parsed.response && typeof parsed.response === 'string') {
                try {
                    parsed.response = JSON.parse(parsed.response);
                } catch (e) {
                    console.log('Failed to parse response string', e);
                }
            }
            this.setState({ response: parsed, errorMessage: '' });
        }).catch((error) => {
            console.log(error);
            this.setState({ response: '', errorMessage: error?.response?.data?.message || error.message || 'Unknown error' });
        }).finally(() => {
            this.setState({ isLoading: false });
        });
    }
}

export default UpdateBenefitDatesSection;
